import { EditRootstockDto } from 'common/api/dto/rootstock.dto';
import { useEditRootstockMutation } from 'common/api/endpoints/variety';
import { handleError } from 'common/api/utils/handleError';
import { Checkbox } from 'common/components/Checkbox/Checkbox';
import FormPrompt from 'common/components/FormPrompt';
import * as notifier from 'common/services/notification';
import { VarFormValues } from 'features/settings-view/hooks/useRootstocksTableData';
import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ModalFormWrapper,
  VarOptionsContainer,
  VarOptionWrapper,
} from './styles';

type VarietySelections = Record<string, boolean>;

export const EditRootstockModal: FC<{
  valuesToEdit: VarFormValues;
  setValuesToEdit: Dispatch<SetStateAction<VarFormValues | null>>;
  varieties: { id: number; code: string }[];
}> = ({ valuesToEdit, setValuesToEdit, varieties }) => {
  const [editRootstock, { isLoading }] = useEditRootstockMutation();

  const defaultValues: VarietySelections = useMemo(() => {
    return varieties.reduce((acc, variety) => {
      const isSelected = !!valuesToEdit.varieties.find(
        v => v.id === variety.id,
      );

      acc[variety.id.toString()] = isSelected;
      return acc;
    }, {} as VarietySelections);
  }, [valuesToEdit, varieties]);

  const methods = useForm<VarietySelections>({
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;

  const closeModal = () => {
    setValuesToEdit(null);
    reset();
  };

  const onSubmit = async (data: VarietySelections): Promise<void> => {
    if (!isDirty) {
      closeModal();
      return;
    }

    const dto = Object.entries(data).reduce<EditRootstockDto>(
      (acc, [varietyId, isSelected]) => {
        if (isSelected) {
          acc.varietyIds.push(parseInt(varietyId, 10));
        }
        return acc;
      },
      { id: valuesToEdit.rStockId, varietyIds: [] },
    );

    try {
      await editRootstock(dto).unwrap();
      notifier.showSuccessMessage(
        `'${valuesToEdit.rStockCode}' varieties updated.`,
      );
      closeModal();
    } catch (error) {
      handleError(
        error,
        `Unable to update '${valuesToEdit.rStockCode}' varieties.`,
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <ModalFormWrapper onSubmit={handleSubmit(onSubmit)}>
        <span>Select &apos;{valuesToEdit.rStockCode}&apos; varieties:</span>
        <VarOptionsContainer>
          {varieties.length > 0 ? (
            varieties.map(variety => (
              <VarOptionWrapper key={variety.code}>
                <Checkbox
                  formProps={{ name: variety.id.toString() }}
                  isDisabled={isLoading}
                />
                <label htmlFor={variety.code.toString()}>{variety.code}</label>
              </VarOptionWrapper>
            ))
          ) : (
            <span>There was an issue fetching varieties.</span>
          )}
        </VarOptionsContainer>
        <div style={{ display: 'flex', gap: '40px' }}>
          <button
            type='button'
            className='cancel-btn'
            onClick={() => setValuesToEdit(null)}
          >
            Close
          </button>
          <button type='submit' className='add-btn'>
            Save
          </button>
        </div>
        <FormPrompt isDirty={isDirty} isSubmitting={isLoading} />
      </ModalFormWrapper>
    </FormProvider>
  );
};
